// Slug → GRASS_CHARACTERISTICS key mapping for guide pages

export interface GuideMetadata {
  slug: string
  grassKey: string
  displayName: string
  season: 'cool' | 'warm'
  shortDescription: string
  zones: string
}

export const GUIDE_SLUGS: Record<string, string> = {
  'kentucky-bluegrass': 'Kentucky Bluegrass',
  'tall-fescue': 'Tall Fescue',
  'fine-fescue': 'Fine Fescue',
  'perennial-ryegrass': 'Perennial Ryegrass',
  'annual-ryegrass': 'Annual Ryegrass',
  'mixed-cool-season-blend': 'Mixed Cool-Season Blend',
  'bermudagrass': 'Bermudagrass',
  'kikuyugrass': 'Kikuyugrass',
  'zoysiagrass': 'Zoysiagrass',
  'st-augustinegrass': 'St. Augustinegrass',
  'centipede': 'Centipede',
  'bahiagrass': 'Bahiagrass',
  'buffalograss': 'Buffalograss',
  'carpetgrass': 'Carpetgrass',
}

export const GUIDE_METADATA: GuideMetadata[] = [
  {
    slug: 'kentucky-bluegrass',
    grassKey: 'Kentucky Bluegrass',
    displayName: 'Kentucky Bluegrass',
    season: 'cool',
    shortDescription: 'The gold standard of cool-season lawns. Dense, self-repairing turf with a distinctive blue-green color that thrives in northern climates.',
    zones: 'Zones 3 to 7',
  },
  {
    slug: 'tall-fescue',
    grassKey: 'Tall Fescue',
    displayName: 'Tall Fescue',
    season: 'cool',
    shortDescription: 'A tough, drought-tolerant cool-season grass with deep roots. Excellent for transition zones and low-maintenance lawns.',
    zones: 'Zones 4 to 7',
  },
  {
    slug: 'fine-fescue',
    grassKey: 'Fine Fescue',
    displayName: 'Fine Fescue',
    season: 'cool',
    shortDescription: 'A fine-textured, shade-tolerant grass perfect for low-traffic areas. Includes creeping red, chewings, hard, and sheep fescue varieties.',
    zones: 'Zones 3 to 7',
  },
  {
    slug: 'perennial-ryegrass',
    grassKey: 'Perennial Ryegrass',
    displayName: 'Perennial Ryegrass',
    season: 'cool',
    shortDescription: 'Fast-germinating, wear-resistant cool-season grass with a glossy dark green appearance. Great for overseeding and quick establishment.',
    zones: 'Zones 4 to 7',
  },
  {
    slug: 'annual-ryegrass',
    grassKey: 'Annual Ryegrass',
    displayName: 'Annual Ryegrass',
    season: 'cool',
    shortDescription: 'A fast-germinating cool-season annual used to keep southern lawns green through winter, then give way as warm-season turf returns.',
    zones: 'Winter overseed in Zones 7 to 10',
  },
  {
    slug: 'mixed-cool-season-blend',
    grassKey: 'Mixed Cool-Season Blend',
    displayName: 'Mixed Cool-Season Blend',
    season: 'cool',
    shortDescription: 'A practical northern lawn mix of bluegrass, ryegrass, and fescue that balances quick establishment, durability, and seasonal resilience.',
    zones: 'Zones 3 to 7',
  },
  {
    slug: 'bermudagrass',
    grassKey: 'Bermudagrass',
    displayName: 'Bermudagrass',
    season: 'warm',
    shortDescription: 'The most popular warm-season grass in America. Aggressive, heat-loving, and incredibly durable for high-traffic southern lawns.',
    zones: 'Zones 7 to 10',
  },
  {
    slug: 'kikuyugrass',
    grassKey: 'Kikuyugrass',
    displayName: 'Kikuyugrass',
    season: 'warm',
    shortDescription: 'An aggressive, fast-repairing warm-season grass built for mild coastal climates, especially Southern California lawns.',
    zones: 'Zones 9 to 11',
  },
  {
    slug: 'zoysiagrass',
    grassKey: 'Zoysiagrass',
    displayName: 'Zoysiagrass',
    season: 'warm',
    shortDescription: 'A dense, carpet-like warm-season grass with excellent cold tolerance for its class. Slow to establish but low-maintenance once mature.',
    zones: 'Zones 6 to 9',
  },
  {
    slug: 'st-augustinegrass',
    grassKey: 'St. Augustinegrass',
    displayName: 'St. Augustinegrass',
    season: 'warm',
    shortDescription: 'A broad-bladed, shade-tolerant warm-season grass dominant in the Gulf Coast and Florida. Lush and tropical-looking.',
    zones: 'Zones 8 to 10',
  },
  {
    slug: 'centipede',
    grassKey: 'Centipede',
    displayName: 'Centipede Grass',
    season: 'warm',
    shortDescription: 'The "lazy man\'s grass," a low-maintenance, slow-growing warm-season option that thrives in acidic soils with minimal fertilization.',
    zones: 'Zones 7 to 9',
  },
  {
    slug: 'bahiagrass',
    grassKey: 'Bahiagrass',
    displayName: 'Bahiagrass',
    season: 'warm',
    shortDescription: 'A tough, deep-rooted warm-season grass built for sandy soils and drought. Popular in Florida and the Gulf Coast for low-input lawns.',
    zones: 'Zones 7 to 11',
  },
  {
    slug: 'buffalograss',
    grassKey: 'Buffalograss',
    displayName: 'Buffalograss',
    season: 'warm',
    shortDescription: 'A native North American prairie grass requiring minimal water and fertilizer. Ideal for eco-friendly lawns in the Great Plains region.',
    zones: 'Zones 3 to 9',
  },
  {
    slug: 'carpetgrass',
    grassKey: 'Carpetgrass',
    displayName: 'Carpetgrass',
    season: 'warm',
    shortDescription: 'A coarse, low-input warm-season grass for wet, acidic soil in warm Southern climates.',
    zones: 'Warm, humid Southern climates',
  },
]

export function getGuideBySlug(slug: string): GuideMetadata | undefined {
  return GUIDE_METADATA.find(g => g.slug === slug)
}

export function getAllGuideSlugs(): string[] {
  return GUIDE_METADATA.map(g => g.slug)
}
